import React from 'react';
import styled from '@emotion/styled';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/auth-provider';
import { can, PERMISSIONS } from '../../../auth/permissions';
import { useDeskInPlan } from '../../hooks/use-desk-in-plan';
import { isCohost } from '../../../auth/roles';
import { IndicatorGroup } from '../../../motion';
import { Tabs, TabIndicator, TabLabel } from '../account/account.styled';
import { layout, palette, radius, shadow, spacing, transition } from '../../../style-utils/theme';
import { font } from '../../../style-utils/text';

/**
 * THE STAYS SECTION TABS — `STAYS_SECTIONS` (ai-plans/stays plan S1;
 * STAYS-COMMON §8.7 R2). Plan S1 OWNS this array; every later plan APPENDS.
 *
 * THE APPEND CONTRACT (read before adding an entry):
 *   - Append at the END. Never reorder, never insert before Listings —
 *     Listings is entry 0 and stays entry 0.
 *   - Every entry carries a `permission`; an entry the session cannot `can()`
 *     is hidden.
 *   - Every path is a NON-UUID slug under /dashboard/stays:
 *     `/dashboard/stays/<slug>`. A uuid tail is a listing page
 *     (dashboard.tsx STAYS_LISTING_PATH) and must never be claimed here.
 *   - Each plan adds its own `case '/dashboard/stays/<slug>'` in dashboard.tsx.
 *   - The planned order after Listings: Messages (S8), Payments (S9; S10 adds
 *     Ledger inside it), Compliance (S11), Metrics / Expenses / Advisor (S13),
 *     Billing / Team (S14), Integrations / API keys (S15), Locks (S16).
 *   - Per-LISTING tabs are S4's `STAYS_LISTING_TABS`, not this array.
 *
 * The active tab is the entry whose path is the LONGEST prefix of the location;
 * any path no other entry claims (a listing page included) marks Listings.
 * With fewer than two visible entries the strip renders nothing, so S1's page
 * looks unchanged until a later plan appends.
 */

export interface StaysSection {
  key: string;
  label: string;
  path: string;
  permission: string;
  /** Independent Stays IS2: 'stays-only' = shown only in an organisation
   *  without Lobbero Desk (utils/products.ts). Absent = every organisation.
   *  IS5: 'cohost' = shown only to a co-host (their own view). */
  only?: 'stays-only' | 'cohost';
}

export const STAYS_SECTIONS: StaysSection[] = [
  { key: 'listings', label: 'Listings', path: '/dashboard/stays', permission: PERMISSIONS.STAYS_READ },
  // Plan S5: the calendar changes waiting for the host, across listings.
  { key: 'sync-review', label: 'Calendar changes', path: '/dashboard/stays/sync-review', permission: PERMISSIONS.STAYS_READ },
  // Plan S8: the organisation's message templates (hidden without stays:manage).
  { key: 'messages', label: 'Messages', path: '/dashboard/stays/messages', permission: PERMISSIONS.STAYS_MANAGE },
  // Independent Stays IS2: a Stays-only host's properties (with Desk the same
  // rows are Buildings and Units, so the tab is not shown).
  {
    key: 'properties',
    label: 'Properties',
    path: '/dashboard/stays/properties',
    // IS5 (IS5-T1): adding and removing places is stays:promote — never a co-host's.
    permission: PERMISSIONS.STAYS_PROMOTE,
    only: 'stays-only',
  },
  // Independent Stays IS4: what Stays costs (the owner's billing:read).
  { key: 'billing', label: 'Billing', path: '/dashboard/stays/billing', permission: PERMISSIONS.BILLING_READ },
  // Independent Stays IS5: the co-hosts and property managers (GET /stays/team,
  // stays:read + user:read — every holder of user:read holds stays:read).
  { key: 'team', label: 'Team', path: '/dashboard/stays/team', permission: PERMISSIONS.USER_READ },
  // Independent Stays IS8: download everything / close the account — the owner
  // (billing:manage) of a Stays-only account (IS8-D3).
  {
    key: 'your-data',
    label: 'Your data',
    path: '/dashboard/stays/your-data',
    permission: PERMISSIONS.BILLING_MANAGE,
    only: 'stays-only',
  },
  // Independent Stays IS5: a co-host's check-in cards across their listings.
  {
    key: 'guest-cards',
    label: 'Guest cards',
    path: '/dashboard/stays/guest-cards',
    permission: PERMISSIONS.STAYS_GUEST_CARDS,
    only: 'cohost',
  },
];

/** The entries this session may see: its permissions, its products and (IS5)
 *  whether it is a co-host. */
export const visibleStaysSections = (
  sections: StaysSection[],
  permissions: string[],
  desk: boolean,
  cohost = false
): StaysSection[] =>
  sections.filter(
    s => can(permissions, s.permission) && !(s.only === 'stays-only' && desk) && !(s.only === 'cohost' && !cohost)
  );

/** Which entry is current for `pathname`: the longest path prefix, else Listings. */
export const activeStaysSection = (sections: StaysSection[], pathname: string): string => {
  const path = pathname.replace(/\/+$/, '') || '/';
  let best: StaysSection | null = null;
  for (const s of sections) {
    if (s.key === 'listings') continue;
    if (path === s.path || path.startsWith(`${s.path}/`)) {
      if (!best || s.path.length > best.path.length) best = s;
    }
  }

  return best ? best.key : 'listings';
};

/* The account page's tab strip, as a plain Link: NavLink would mark Listings
   active on every /dashboard/stays/* path, and the rule here is the longest
   prefix. Same tokens as account.styled.tsx Tab. */
const StaysTab = styled(Link)`
  position: relative;
  isolation: isolate;
  flex: 1 1 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: ${layout.touchTarget};
  box-sizing: border-box;
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.sm};
  font-family: ${font.family};
  font-size: ${font.size.xs}px;
  font-weight: 600;
  color: ${palette.slate};
  text-align: center;
  text-decoration: none;
  transition: ${transition.colors};

  &:hover {
    color: ${palette.ink};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  &[aria-current='page'] {
    color: ${palette.ink};
  }
`;

export const StaysSectionTabs: React.FC<{ sections?: StaysSection[] }> = ({ sections = STAYS_SECTIONS }) => {
  const { permissions, user } = useAuth();
  const desk = useDeskInPlan();
  const { pathname } = useLocation();
  const visible = visibleStaysSections(sections, permissions, desk, isCohost(user?.role));
  if (visible.length < 2) return null;
  const active = activeStaysSection(visible, pathname);

  return (
    <Tabs aria-label="Stays sections">
      <IndicatorGroup id="stays-section-tabs">
        {visible.map(s => (
          <StaysTab key={s.key} to={s.path} aria-current={s.key === active ? 'page' : undefined}>
            {s.key === active ? <TabIndicator id="stays-section-tabs" data-active-indicator="true" /> : null}
            <TabLabel>{s.label}</TabLabel>
          </StaysTab>
        ))}
      </IndicatorGroup>
    </Tabs>
  );
};
